/**
 * Social sign-in, and the two sentences that decide whether it works.
 *
 * WHAT THIS PAGE IS FOR
 *
 * Handing a customer the exact URL to paste into Google's console, and
 * telling them what happens if they do not. A redirect URI that does not
 * match produces an error page at Google naming neither the URL it
 * expected nor the one it received, which is a genuinely undiagnosable
 * failure for somebody who has never done it before.
 *
 * WHY THE SECRET FIELD IS NOT PREFILLED
 *
 * It cannot be: nothing returns it, ever. The form therefore always looks
 * empty, which reads like "not configured" unless the page says otherwise -
 * so a configured provider shows its client id and the date it was set, and
 * the secret box says it replaces rather than reveals.
 */

import type { AuthProvider } from './api.js'

export const PROVIDERS = [
  {
    id: 'google' as const,
    label: 'Google',
    console: 'https://console.cloud.google.com/apis/credentials',
    /** Where the callback URL goes, in that console's own words. */
    field: 'Authorised redirect URIs',
  },
  {
    id: 'github' as const,
    label: 'GitHub',
    console: 'https://github.com/settings/developers',
    field: 'Authorization callback URL',
  },
]

export type ProviderId = (typeof PROVIDERS)[number]['id']

/** What is configured, indexed for the page. */
export const byId = (social: AuthProvider[] | undefined): Map<string, AuthProvider> =>
  new Map((social ?? []).map((p) => [p.provider, p]))

export type CredentialProblem = { field: 'clientId' | 'clientSecret'; why: string }

/**
 * Mistakes worth catching before a round trip.
 *
 * The API refuses these too - this is not the boundary - but a 400 is a
 * worse way to learn you pasted the client id into both boxes than a
 * sentence beside the box.
 */
export function checkCredentials(input: { clientId: string; clientSecret: string }): CredentialProblem[] {
  const clientId = input.clientId.trim()
  const clientSecret = input.clientSecret.trim()
  const problems: CredentialProblem[] = []

  if (clientId === '') problems.push({ field: 'clientId', why: 'A client id is needed.' })
  if (clientSecret === '') problems.push({ field: 'clientSecret', why: 'A client secret is needed.' })

  // Both halves or neither is the rule everywhere else in this feature; a
  // provider with one half configured fails at the redirect, which is a
  // worse discovery than not offering it at all.
  if (clientId !== '' && clientSecret !== '' && clientId === clientSecret) {
    problems.push({
      field: 'clientSecret',
      why: 'The client id and secret are the same value. The secret is the other one.',
    })
  }
  return problems
}

/**
 * What to say after saving.
 *
 * `live` false means the credentials are stored and the running auth
 * service has not been updated - a state that must never be shown as
 * success, because the customer's next step is to test a sign-in that will
 * still use the old credentials.
 */
export function savedMessage(result: { live: boolean }, label: string): {
  tone: 'ok' | 'warn'; text: string
} {
  return result.live
    ? {
      tone: 'ok',
      text: `${label} is configured. Paste the callback URL into its console, or sign-in fails at the redirect.`,
    }
    : {
      tone: 'warn',
      text: `${label} is saved but NOT live: the auth service could not be updated. `
        + 'Turn auth off and on again to apply it.',
    }
}

/**
 * What ending every session actually does, for the confirmation.
 *
 * Written out because "revoke sessions" and "disable auth" sound
 * interchangeable and are not: one signs everybody out now, the other
 * stops the service and KEEPS every session for when it comes back.
 */
export const REVOKE_CONSEQUENCES = [
  'Everyone using your app is signed out immediately, and no existing login can get a new token.',
  // Five, not fifteen and not zero. The edge caches the signing keys for
  // five minutes, so a token already in somebody's hands outlives the
  // revoke by up to that long. Somebody reading this during a breach is
  // deciding whether to wait, and a wrong number decides it for them.
  'Tokens already issued stop working within five minutes, once the edge picks up the new signing keys.',
  'Accounts, passwords and data are untouched — people simply sign in again.',
]

/** What disabling does, which is the one people get wrong. */
export const DISABLE_CONSEQUENCES = [
  'Sign-in stops working for your app.',
  'Your users are kept, and so are their sessions — turning auth back on signs them all back in.',
  'To end every session now, revoke them instead.',
]

/**
 * Whether a forgotten password can be recovered.
 *
 * A project whose deployment has no email provider cannot send a reset, and
 * the first person to find out is otherwise one of the customer's users, at
 * the moment they are locked out of their own account.
 */
export function recoveryNote(recovery: 'email' | 'unavailable' | undefined): string | null {
  if (recovery !== 'unavailable') return null
  return 'Password reset is unavailable: this deployment has no email provider, so a user who '
    + 'forgets their password cannot recover it. Sign-in and sign-up work normally.'
}
